import { Observable, Subscription, zip } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  AppState,
  getLoadingPartyState,
  loadedCategoriesListPartyState,
  loadedEmployeeListTotalState,
  loadedEmployeePartyListExcelState,
  loadedEmployeePartyListState,
  loadedPropertiesListPartyState,
} from "src/app/store/reducers";
import {
  getCategoriesForListByUsage,
  getEmployeeTableConfig,
  getPropertiesForListByUsage,
  saveEmployeeTableConfig,
} from "src/app/store/actions/parties/parties.actions";

import { EmployeePartyDTO } from "src/app/models/Party/employeePartyDTO";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";

import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  ExportEvent,
  FieldType,
  FormatType,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";

import { setCategories, setProperties } from "../../utils/tableUtils";
import { CHIP_STYLE, getChipStyle } from "../../utils/customTableUtils";

const USAGE_CODE = "EMPLOYEE";

export class BaseEmployeesList {
  isLoading$: Observable<boolean>;

  columnInfo$: Observable<ColumnSorterInfo[]>;

  employeeList: EmployeePartyDTO[];
  employeeList$: Observable<EmployeePartyDTO[]>;
  excelEmployeeList$: Observable<EmployeePartyDTO[]>;
  totalEmployees$: Observable<number>;

  loadedEmployeesSub: Subscription;
  multiSubs: Subscription;

  categories$: Observable<CategoryDTO[]>;
  properties$: Observable<PropertyDTO[]>;

  formPath: string = "employees/employee";
  tableTitle: string;
  headerIcon: string;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Employees.code",
      field: "code",
      fieldType: FieldType.STRING,
      searchable: true,
      hidden: true,
      identifier: true,
    },
    {
      header: "Employees.employeeCode",
      field: "employeeCode",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Employees.Status",
      field: "statusDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Employees.VatIN",
      field: "vatin",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Employees.FullName",
      field: "name",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Employees.EmployeerCode",
      field: "customer.customerCode",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Employees.Employeer",
      field: "customer.name",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Employees.EmployeerNif",
      field: "customer.vatin",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Employees.Salutation",
      field: "salutationDescription",
      fieldType: FieldType.STRING,
    },
    {
      header: "Employees.Birthday",
      field: "birthday",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATE,
      hidden: true,
    },
    {
      header: "Employees.Phone",
      field: "contacts[PHONE].contact",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Employees.Email",
      field: "contacts[EMAIL].contact",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Employees.Mobile",
      field: "contacts[MOBILE].contact",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Employees.StreetName",
      field: "address.street",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Employees.ZipCode",
      field: "address.postalZipCode",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Employees.Country",
      field: "address.isoCountryCode",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Employees.AddressDesc",
      field: "address.description",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Employees.CreatedOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
    {
      header: "Employees.Updated",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
  ];

  constructor(public store: Store<AppState>) {
    this.isLoading$ = this.store.pipe(select(getLoadingPartyState));
    this.employeeList$ = this.store.pipe(select(loadedEmployeePartyListState));
    this.categories$ = this.store.pipe(select(loadedCategoriesListPartyState));
    this.properties$ = this.store.pipe(select(loadedPropertiesListPartyState));
    this.excelEmployeeList$ = this.store.pipe(
      select(loadedEmployeePartyListExcelState)
    );
    this.totalEmployees$ = this.store.pipe(
      select(loadedEmployeeListTotalState)
    );
  }

  getEmployeeCategoriesProperties(
    usageCode: string,
    getEmployeesList: Function
  ) {
    this.multiSubs = zip(
      this.categories$.pipe(skip(1)),
      this.properties$.pipe(skip(1))
    ).subscribe(([categories, properties]) => {
      if (categories && categories?.length) {
        const tableConfigCategories = setCategories(categories);
        if (tableConfigCategories) {
          this.tableConfig = this.tableConfig.concat(tableConfigCategories);
        }
      }

      if (properties && properties?.length) {
        const tableConfigProperties = setProperties(properties);
        if (tableConfigProperties) {
          this.tableConfig = this.tableConfig.concat(tableConfigProperties);
        }
      }

      this.getTableConfig(usageCode);
      getEmployeesList();
      this.setTableCustomElements();
    });

    this.store.dispatch(
      getCategoriesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );

    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
  }

  setTableCustomElements() {
    this.loadedEmployeesSub = this.employeeList$
      .pipe(skip(1))
      .subscribe((employees: EmployeePartyDTO[]) => {
        this.employeeList = employees;
        if (
          this.employeeList !== undefined &&
          this.employeeList !== null &&
          this.employeeList?.length > 0
        ) {
          this.employeeList = this.employeeList.map((employee) => {
            let treatedEmployee = employee;
            treatedEmployee = this.setCustomStatusChip(treatedEmployee);
            return treatedEmployee;
          });
        }
      });
  }

  setCustomStatusChip(employee: any) {
    const defaultCustomer = {
      ...employee,
    };
    const { statusDescription, statusCode } = employee;
    if (statusDescription === undefined) {
      return defaultCustomer;
    }

    let style: CHIP_STYLE = CHIP_STYLE.INACTIVE;
    if (statusDescription !== undefined && statusDescription !== null) {
      if (statusCode !== undefined && statusCode !== null) {
        switch (statusCode) {
          case "ACTIVE":
            style = CHIP_STYLE.SUCCESS;
            break;
          case "DISABLED":
            style = CHIP_STYLE.INACTIVE;
            break;
          case "BLOCKED":
            style = CHIP_STYLE.DANGER;
            break;
        }
      }

      let styleSettings = getChipStyle(style);
      return {
        ...employee,
        statusDescription: {
          value: statusDescription,
          icon: styleSettings.icon,
          style: styleSettings.style,
        },
      };
    }
  }

  getTableConfig(usageCode: string) {
    this.store.dispatch(
      getEmployeeTableConfig({
        options: {
          code: usageCode,
          settingType: ConfigSettingType.TABLE_CONFIG,
        },
      })
    );
  }

  getTableData(tableEvent: TableEvent, getEmployeesList: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    getEmployeesList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  getExportData(exportEvent: ExportEvent, getEmployeesExcelList: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    getEmployeesExcelList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  saveTableConfig(tableColumnInfo: ColumnSorterInfo[], usageCode: string) {
    this.store.dispatch(
      saveEmployeeTableConfig({
        config: {
          code: usageCode,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }
}
